import { EMPTY, Observable, Observer, of, throwError } from 'rxjs';
import { catchError, expand, map } from 'rxjs/operators';
import { LogLevel } from '../diagnostics/log-level';
import { LogRecord } from '../diagnostics/log-record';
import { Logging } from '../diagnostics/logging';
import { SmeWebTelemetry } from '../diagnostics/sme-web-telemetry';
import { TelemetryEventStates } from '../diagnostics/sme-web-telemetry-models';
import { Strings } from '../generated/strings';
import { BatchConnection, BatchRequest, BatchResponseItem } from './batch-connection';
import { Disposable, DisposableLifetimeManager, Disposer } from './disposable';
import { HttpMethod } from './http';
import { Net } from './net';
import { PowerShell, PowerShellCommand, PowerShellOptions, PowerShellSessionRequestOptions } from './powershell';

/**
 * PowerShell batch response Item.
 */
export interface PowerShellBatchResponseItem {

    /**
     * The request Sequence number to which this response correspond to.
     */
    sequenceNumber: number;

    /**
     * The status of the specific call to a node.
     */
    status: number;

    /**
     * The node name returning the response.
     */
    nodeName: string;

    /**
     * The Json properties returned from node.
     */
    properties: any;

    /**
     * The error json obj, in case of error.
     */
    error?: any;

    /**
     * The errors json array obj, in case of aggregated errors.
     */
    errors?: any[];
}

/**
 * PowerShell context object interface.
 */
interface PowerShellBatchContext {

    /**
     * The shared key name for runspace.
     */
    key: string;

    /**
     * The nodes list targeted by this PowerShellBatch object.
     */
    nodesList: string[];

    /**
     * The array of referenced containers.
     */
    lifetimes: DisposableLifetimeManager[];

    /**
     * The request options for the powershell session
     */
    requestOptions: PowerShellSessionRequestOptions;
}

/**
 * PowerShell batch saved runspace session for a node.
 */
interface RunspaceSessionContext {

    /**
     * The runspace session Id.
     */
    sessionId: string;

    /**
     * The runspace creation time. Used to check if session is expired.
     */
    creationTimestamp: number;
}

/**
 * PowerShell runspace session state.
 */
enum RunspaceSessionState {
    /**
     * Runspace still active.
     */
    Active,

    /**
     * Runspace already expired.
     */
    Expired,

    /**
     * No runsapce available for the give node.
     * Either this is the first call, or the previous call error out
     * or previous runspace was deleted due to expiry.
     */
    Unavailable
}

/**
 * PowerShell command queue item.
 */
interface PowerShellBatchCommandItem {
    /**
     * The nodes to execute command against.
     */
    nodesList: string[];
    /**
     * The command list to execute.
     */
    commandList: string[];

    /**
     * Options for how to handle the command (reserved)
     */
    options?: PowerShellOptions;

    /**
     * Deferred object currently waiting for command run.
     */
    observer: Observer<any>;
}

/**
 * The PowerShellBatchSession class.
 */
export class PowerShellBatchSession implements Disposable {
    /**
     * Initializes a new instance of the PowerShellBatchSession class.
     *
     * @param powerShellBatch the PowerShellBatch object.
     * @param lifetime the disposable lifetime manager object.
     */
    public constructor(powerShellBatch: PowerShellBatch);
    public constructor(powerShellBatch: PowerShellBatch, lifetime: DisposableLifetimeManager);
    public constructor(public powerShellBatch: PowerShellBatch, private lifetime?: DisposableLifetimeManager) {
    }

    /**
     * Dispose the session object.
     */
    public dispose(): void {
        if (this.lifetime) {
            this.lifetime.dispose();
        }
    }
}

/**
 * Class containing methods related to PowerShell runspaces creation/deletion/command using PowerShell Raw API plugin during batch run.
 *  - It's auto holding the runspace as long as it's used within last 3 minutes.
 */
class PowerShellBatchRaw implements Disposable {
    // 3 minutes session holding time.
    private static maxDeltaTimeInMs: number = 3 * 60 * 1000;
    private nodesToSessionIdsMap: MsftSme.StringMap<RunspaceSessionContext> = {};
    private timestampInMs = 0;
    private markDelete = false;
    private internalActive = false;
    private requestedNodesList: string[];

    /**
     * Initializes a new instance of the PowerShellBatchRaw class.
     *
     * @param batchConnection The batch connection service.
     * @param context The PowerShell batch session Context.
     */
    constructor(private batchConnection: BatchConnection, private context: PowerShellBatchContext) {

    }

    /**
     * Gets active status of PowerShell execution.
     */
    public get active(): boolean {
        return this.internalActive;
    }

    /**
     * Dispose the runspace.
     */
    public dispose(): void {
        if (!this.active) {
            // only close sessions that have been created.
            // If a result was cached a component may not
            // execute a command and still dispose the session
            // when the component is destroyed.
            if (Object.keys(this.nodesToSessionIdsMap).length > 0) {
                this.close().subscribe();
            }
        } else {
            this.markDelete = true;
        }
    }

    /**
     * Runs the given batch command, and try followup Get calls if all nodes don't complete during the initial batch call.
     *
     * @param nodesList The nodes list to run batch against.
     * @param commandList The list of command body, corresponding to nodesList.
     */
    public runCommand(nodesList: string[], commandList: string[]): Observable<PowerShellBatchResponseItem[]> {
        // take the timestamp only success/healthy case.
        // error session would be auto-deleted after expiration time.
        this.internalActive = true;
        this.requestedNodesList = nodesList;

        return this.command(nodesList, commandList)
            .pipe(
                catchError((error) => {
                    this.internalActive = false;
                    SmeWebTelemetry.tracePowershellBatchEvent(commandList, TelemetryEventStates.Error, error);
                    return throwError(() => error);

                }),
                expand((response: BatchResponseItem[]) => {
                    this.timestampInMs = Date.now();

                    const psBatchResponse: PowerShellBatchResponseItem[] =
                        this.convertBatchResponseToPowerShellBatchResponse(response);

                    const incompleteNodes: string[] = this.getIncompleteNodes(psBatchResponse);
                    if (incompleteNodes.length === 0) {
                        this.internalActive = false;
                        return EMPTY;
                    }

                    // create list of Get URLs for incomplete nodes.
                    const incompleteNodesUrlList = this.createRelativeUrlListSingleMethod(incompleteNodes, HttpMethod.Get);

                    // update requested Nodes list, so we can parse the response correctly.
                    this.requestedNodesList = incompleteNodesUrlList;
                    return this.batchConnection.get(incompleteNodes, incompleteNodesUrlList, <BatchRequest>this.context.requestOptions);

                }),
                map((response: BatchResponseItem[]) => {
                    const psBatchResponse: PowerShellBatchResponseItem[] =
                        this.convertBatchResponseToPowerShellBatchResponse(response);

                    const errorResponses =
                        psBatchResponse.filter((psResponse) => psResponse.error || psResponse.errors);

                    if (errorResponses.length > 0) {
                        SmeWebTelemetry.tracePowershellBatchEvent(commandList,
                            TelemetryEventStates.Error,
                            {response: errorResponses});
                    }

                    return psBatchResponse;
                }));
    }

    /**
     * Close/Delete the session / runspace map.
     */
    public close(): Observable<PowerShellBatchResponseItem[]> {
        if (Object.keys(this.nodesToSessionIdsMap).length > 0) {

            const nodeList: string[] = [];
            for (const node in this.nodesToSessionIdsMap) {
                if (this.nodesToSessionIdsMap.hasOwnProperty(node)) {
                    nodeList.push(node);
                }
            }

            const nodeUrls = this.createRelativeUrlListSingleMethod(nodeList, HttpMethod.Delete);

            return this.batchConnection.delete(nodeList, nodeUrls, <BatchRequest>this.context.requestOptions)
                .pipe(
                    map((responseData: BatchResponseItem[]) => {
                        this.nodesToSessionIdsMap = {};
                        const psBatchResponse: PowerShellBatchResponseItem[]
                            = this.convertBatchResponseToPowerShellBatchResponse(responseData);
                        return psBatchResponse;
                    }));
        }

        Logging.log({
            level: LogLevel.Warning,
            source: 'PowerShellBatch/close',
            message: MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.PowerShellUnableSessionClose.message
        });
        return of(null);
    }

    /**
     * Cancel the command.
     */
    public cancelCommand(): Observable<PowerShellBatchResponseItem[]> {
        if (Object.keys(this.nodesToSessionIdsMap).length > 0) {

            const nodeList: string[] = [];
            for (const node in this.nodesToSessionIdsMap) {
                if (this.nodesToSessionIdsMap.hasOwnProperty(node)) {
                    nodeList.push(node);
                }
            }

            const nodeUrls = this.createRelativeUrlListSingleMethod(nodeList, 'CANCEL');

            return this.batchConnection.put(nodeList, nodeUrls)
                .pipe(map((responseData: any) => {
                    this.nodesToSessionIdsMap = {};
                    const psBatchResponse: PowerShellBatchResponseItem[] = this.convertBatchResponseToPowerShellBatchResponse(responseData);
                    return psBatchResponse;
                }));
        }

        Logging.log({
            level: LogLevel.Warning,
            source: 'PowerShell',
            message: MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.PowerShellUnableCancelCommand.message
        });
        return of(null);
    }

    /**
     * Parse the response array for multi-part response and convert to PowerShellBatchResponse list.
     *
     * @param responseList The BatchResponse array received for the powershell batch call.
     */
    private convertBatchResponseToPowerShellBatchResponse(responseList: BatchResponseItem[]): PowerShellBatchResponseItem[] {

        const powershellBatchResponse: PowerShellBatchResponseItem[] = [];

        for (let itemId = 0; itemId < responseList.length; itemId++) {

            const responseItem = responseList[itemId].response;
            const nodeName = responseList[itemId].nodeName;
            const sequenceNumber = responseList[itemId].sequenceNumber;
            const jsonResponse = responseItem.response;
            const status = responseItem.status;
            const properties: any = Net.getItemProperties(jsonResponse);

            if (status < 400) {
                powershellBatchResponse.push(<PowerShellBatchResponseItem>{ sequenceNumber, status, nodeName, properties });

            } else {
                const responseData = responseItem.response;
                if (responseData.error) {
                    const error: any = responseData.error;
                    powershellBatchResponse.push(<PowerShellBatchResponseItem>{ sequenceNumber, status, nodeName, properties, error });
                    Logging.log(<LogRecord>{
                        source: 'Batch PowerShell',
                        level: LogLevel.Error,
                        message: MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.BatchConnection.message
                            .format(status, error.code, Net.getPowerShellErrorMessage(responseItem.response))
                    });
                } else if (responseData.errors) {
                    const errors: any = responseData.errors;
                    powershellBatchResponse.push(<PowerShellBatchResponseItem>{ sequenceNumber, status, nodeName, properties, errors });
                    Logging.log(<LogRecord>{
                        source: 'Batch PowerShell',
                        level: LogLevel.Error,
                        message: Net.getPowerShellErrorMessage(responseItem.response)
                    });
                }
            }
        }

        return powershellBatchResponse;
    }

    /**
     * Initiate command execution. It auto recycles old sessions.
     *
     * @param nodesList The list of nodes to run commands against
     * @param commandList The command body list corresponding to nodesList.
     */
    private command(nodesList: string[], commandList: string[]): Observable<BatchResponseItem[]> {

        const nodesSessionStateMap: MsftSme.StringMap<RunspaceSessionState> = this.getSessionsStateForNodesList(nodesList);
        const methodsList: string[] = [];

        for (let index = 0; index < nodesList.length; index++) {

            const nodeName = nodesList[index];

            if (nodesSessionStateMap[nodeName] === RunspaceSessionState.Expired) {
                // Delete item from map.
                delete this.nodesToSessionIdsMap[nodeName];
            }

            if (nodesSessionStateMap[nodeName] === RunspaceSessionState.Active) {
                // Post method
                methodsList.push(HttpMethod.Post);

            } else {
                // Put method
                methodsList.push(HttpMethod.Put);
            }
        }

        const nodeUrls = this.createRelativeUrlList(nodesList, methodsList);
        return this.batchConnection.mixed(nodesList, nodeUrls, commandList, methodsList, <BatchRequest>this.context.requestOptions);
    }

    /**
     * Check if a valid/non-expired sesison exists for each node in the list.
     *
     * @param nodesList The nodes list to check valid existing sesion for.
     */
    private getSessionsStateForNodesList(nodesList: string[]): MsftSme.StringMap<RunspaceSessionState> {

        const runspaceSessionsState: MsftSme.StringMap<RunspaceSessionState> = {};

        for (let index = 0; index < nodesList.length; index++) {
            const savedSession: RunspaceSessionContext = this.nodesToSessionIdsMap[nodesList[index]];
            if (!savedSession) {
                runspaceSessionsState[nodesList[index]] = RunspaceSessionState.Unavailable;

            } else if (this.isSessionEntryExpired(savedSession)) {
                runspaceSessionsState[nodesList[index]] = RunspaceSessionState.Expired;

            } else {
                runspaceSessionsState[nodesList[index]] = RunspaceSessionState.Active;
            }
        }
        return runspaceSessionsState;
    }

    /**
     * Create a relative url list for PowerShell Post batch call, based on nodes and methods list.
     *
     * @param nodesList The list of nodes to generate urls for
     * @param methodList The http method types map corresponding to nodesList.
     */
    private createRelativeUrlList(nodesList: string[], methodList: string[]): string[] {

        const responseUrlList: string[] = [];

        for (let index = 0; index < nodesList.length; index++) {

            const nodeName = nodesList[index];
            const method = methodList[index];

            // try to get session ids for given Node from the stored map.
            const savedSession: RunspaceSessionContext = this.nodesToSessionIdsMap[nodeName];
            const sessionId = (savedSession && !this.isSessionEntryExpired(savedSession)) ? savedSession.sessionId : MsftSme.newGuid();

            responseUrlList.push(this.createRelativeUrl(method, sessionId));
        }

        return responseUrlList;
    }

    /**
     * Create a relative url list for PowerShell batch call, based on provided method.
     *
     * @param nodesList The list of nodes to generate urls for
     * @param method The http method type
     */
    private createRelativeUrlListSingleMethod(nodesList: string[], method: string): string[] {

        const responseUrlList: string[] = [];

        for (let index = 0; index < nodesList.length; index++) {

            const nodeName = nodesList[index];

            // try to get session ids for given Node from the stored map.
            const savedSession: RunspaceSessionContext = this.nodesToSessionIdsMap[nodeName];
            const sessionId = (savedSession && !this.isSessionEntryExpired(savedSession)) ? savedSession.sessionId : MsftSme.newGuid();

            responseUrlList.push(this.createRelativeUrl(method, sessionId));
        }

        return responseUrlList;
    }

    /**
     * Create a relative url for the given method and session Id.
     *
     * @param method The Http method to use for call.
     * @param sessionId The PS runspace session Id.
     */
    private createRelativeUrl(method: string, sessionId: string): string {

        let relativeUrl = '';

        if (method === HttpMethod.Delete) {
            relativeUrl = Net.powerShellApiSessions.format(sessionId);

        } else if (method === HttpMethod.Put) {
            relativeUrl = Net.powerShellApiSessions.format(sessionId);

        } else if (method === HttpMethod.Post) {
            relativeUrl = Net.powerShellApiExecuteCommand.format(sessionId);

        } else if (method === HttpMethod.Get) {
            relativeUrl = Net.powerShellApiRetrieveOutput.format(sessionId);

        } else if (method === 'CANCEL') {
            relativeUrl = Net.powerShellApiCancelCommand.format(sessionId);
        }

        return relativeUrl;
    }

    /**
     * Check if all indivudual nodes have returned 'completed' result and return the list of nodes which returned 'completed=false'
     *
     * @param responseArray The response from a PowerShell batch call.
     * @return incompleteNodes The incompleteNodes array populated with nodes not completed yet.
     */
    private getIncompleteNodes(responseArray: PowerShellBatchResponseItem[]): string[] {

        const incompleteNodes: string[] = [];
        for (const responseItem of responseArray) {

            const properties: any = responseItem.properties;

            // skip the error cases.
            if (!properties) {
                continue;
            }

            const sessionId: string = properties.sessionId;
            const creationTimestamp: number = Date.now();

            if (sessionId) {
                // keep the PS session GUID
                this.nodesToSessionIdsMap[responseItem.nodeName] = <RunspaceSessionContext>{ sessionId, creationTimestamp };
            }

            if (properties.completed && properties.completed.toLowerCase() !== 'true') {
                incompleteNodes.push(responseItem.nodeName);
            }
        }

        return incompleteNodes;
    }

    /**
     * Checks if a stored runSpace session for a specific node is expired.
     *
     * @param rsSessionContext runspace session context.
     */
    private isSessionEntryExpired(rsSessionContext: RunspaceSessionContext): boolean {
        const now = Date.now();
        return rsSessionContext.creationTimestamp !== 0 && (now - rsSessionContext.creationTimestamp) > PowerShellBatchRaw.maxDeltaTimeInMs;
    }
}

/**
 * The PowerShellbatch class.
 *
 * - Single instance of PowerShell batch class manages a single single nodes-runspaces map, with a runspace corresponding to each node.
 * - It queues coming requests and process one at a time sequentially.
 * - If a command is slow and causing with multiple responses, it aggregates response into single Q result.
 * - A PowerShellBatch instance should be created through create() function, and it's statically stored/managed into _map collection.
 * - Once all lifetime references are gone, it deletes the runspaces map.
 * - To dispose the PowerShellBatch instance, it can use lifetime.dispose().
 */
export class PowerShellBatch {
    /**
     * Static collection of PowerShellbatch objects.
     */
    private static map: MsftSme.StringMap<PowerShellBatch> = {};

    /**
     * The context of PowerShellBatch object.
     */
    private context: PowerShellBatchContext;

    /**
     * The queue of PowerShell command requests.
     */
    private queue: PowerShellBatchCommandItem[] = [];

    /**
     * The reference to PowerShellRaw class object.
     */
    private raw: PowerShellBatchRaw;

    /**
     * Current data to return to caller.
     */
    private currentData: PowerShellBatchResponseItem[] = [];

    /**
     * Current data map to aggregate partial data parts from multiple data responses.
     */
    private currentDataMap: MsftSme.StringMap<PowerShellBatchResponseItem> = {};

    /**
     * Timestamp when last command started.
     */
    private timestamp: number;

    /**
     * Find or create new PowerShellbatch object.
     *
     * @param nodesList The nodes list targeted by this PowerShellBatch object.
     * @param batchConnection The batch connection.
     * @param key The shared key to queue the requests to use the single nodes-runspaces map.
     * @param lifetime The lifetime container.
     * @param requestOptions the options to apply to every request in this session
     */
    public static create(nodesList: string[], batchConnection: BatchConnection): PowerShellBatch;
    public static create(nodesList: string[], batchConnection: BatchConnection, key: string, lifetime: DisposableLifetimeManager): PowerShellBatch;
    public static create(nodesList: string[], batchConnection: BatchConnection, key: string, lifetime: DisposableLifetimeManager,
        requestOptions: PowerShellSessionRequestOptions): PowerShellBatch;
    public static create(
        nodesList: string[],
        batchConnection: BatchConnection,
        key?: string,
        lifetime?: DisposableLifetimeManager,
        requestOptions?: PowerShellSessionRequestOptions): PowerShellBatch {
        let ps: PowerShellBatch;

        if (key && lifetime) {
            ps = PowerShellBatch.map[PowerShellBatch.indexName(nodesList, key)];
            if (ps) {
                ps.addLifetime(lifetime);
                return ps;
            }
        }

        ps = new PowerShellBatch(nodesList, batchConnection, key, lifetime, requestOptions);
        if (key && lifetime) {
            PowerShellBatch.map[PowerShellBatch.indexName(nodesList, key)] = ps;
        }

        return ps;
    }

    /**
     * Find existing PowerShellBatch object. Create call must be called before to create the PowerShellBatch instance.
     *
     * @param nodeName The node name.
     * @param key The shared key to queue the requests to use the single runspace.
     */
    public static find(nodesList: string[], key: string): PowerShellBatch {
        return PowerShellBatch.map[PowerShellBatch.indexName(nodesList, key)];
    }

    /**
     * Create the index name in map collection.
     *
     * @param nodesList The nodes list targeted by this PowerShellBatch object.
     * @param key The shared key to queue the requests to use the single runspace.
     */
    private static indexName(nodesList: string[], key: string): string {
        return nodesList.join(':') + ':' + key;
    }

    /**
     * Initializes a new instance of the PowerShellBatch class.
     * (private constructor which shouldn't be called directly.)
     *
     * @param nodeList The nodes list targeted by this PowerShellBatch object.
     * @param batchConnection The batch connection service.
     * @param key The shared key to queue the requests to use the single runspace map.
     * @param lifetime The lifetime container.
     */
    constructor(
        nodeList: string[],
        batchConnection: BatchConnection,
        key: string,
        lifetime: DisposableLifetimeManager,
        options: PowerShellSessionRequestOptions) {
        this.context = {
            key: key,
            nodesList: nodeList,
            lifetimes: [],
            requestOptions: PowerShell.newEndpointOptions(options)
        };
        this.timestamp = 0;
        this.raw = new PowerShellBatchRaw(batchConnection, this.context);
        if (key && lifetime) {
            lifetime.registerForDispose(new Disposer(() => this.lifetimeDisposer(lifetime)));
            this.context.lifetimes.push(lifetime);
        }
    }

    /**
     * Run PowerShellBatch command.
     *
     * @param command The command to run against all nodes in nodesList.
     * @param options The options.
     * @return observable The result of PowerShell batch command.
     */
    public runSingleCommand(command: PowerShellCommand, options?: PowerShellOptions): Observable<PowerShellBatchResponseItem[]> {
        const commandsList: PowerShellCommand[] = [];
        for (let i = 0; i < this.context.nodesList.length; i++) {
            commandsList.push(command);
        }

        return this.run(commandsList, options);
    }

    /**
     * Run PowerShellBatch command list.
     *
     * @param commandsList The commands to run against given nodesList.
     * @param options The options.
     * @return observable The result of PowerShell batch command.
     */
    public run(commandsList: PowerShellCommand[], options?: PowerShellOptions): Observable<PowerShellBatchResponseItem[]> {
        if (commandsList.length !== this.context.nodesList.length) {
            return EMPTY;
        }

        const commandBodyList: string[] = [];
        // wrap command in properties.
        for (const command of commandsList) {
            commandBodyList.push(Net.createPropertiesJSONString(command));
        }

        if (this.context.lifetimes.length === 0) {
            // no disposer is assigned, force to close the session after every query.
            const timeoutMs: number = options && options.timeoutMs;
            if (options) {
                options.timeoutMs = timeoutMs;
                options.close = true;
            } else {
                options = { timeoutMs: timeoutMs, close: true };
            }
        }
        // queue the request.
        const observable = this.enqueue(this.context.nodesList, commandBodyList, options);
        return observable;
    }

    /**
     * Cancel PowerShellBatch command.
     */
    public cancel(): Observable<PowerShellBatchResponseItem[]> {
        return this.raw.cancelCommand();
    }

    /**
     * Enqueue a command request.
     *
     * @param nodesList: the node list.
     * @param commandBodyList The command.
     * @param options The options.
     */
    private enqueue(
            nodesList: string[],
            commandBodyList: string[],
            options?: PowerShellOptions): Observable<PowerShellBatchResponseItem[]> {
        return new Observable((observer: Observer<PowerShellBatchResponseItem[]>) => {
            this.queue.push(<PowerShellBatchCommandItem>{ nodesList, commandList: commandBodyList, observer, options });
            this.dequeue();
        });
    }

    /**
     * Dequeue a command request.
     */
    private dequeue(): boolean {
        if (this.raw.active) {
            return false;
        }

        const item: PowerShellBatchCommandItem = this.queue.shift();
        if (item) {
            this.currentData = [];
            this.currentDataMap = {};
            this.timestamp = Date.now();
            this.raw.runCommand(item.nodesList, item.commandList).subscribe({
                next: data => {

                    this.collect(
                        data,
                        item.options && item.options.timeoutMs,
                        item.options && item.options.partial ? item.observer : null);
                },
                error: error => {
                    if (item.options && item.options.close) {
                        this.raw.close().subscribe();
                    }

                    item.observer.error(error);
                    this.timestamp = 0;
                    this.dequeue();
                },
                complete: () => {
                    if (item.options && item.options.close) {
                        this.raw.close().subscribe();
                    }

                    if (!item.options || !item.options.partial) {
                        item.observer.next(this.currentData);
                    }

                    item.observer.complete();
                    this.timestamp = 0;
                    this.dequeue();
                }
            });
            return true;
        }

        return false;
    }

    /**
     * Collect response results for batch call and aggregate into single object.
     *
     * @param properties The properties of response object.
     * @param timeoutMs The timeout to cancel command.
     * @param observer The observer of powershell results.
     */
    private collect(psResponseList: PowerShellBatchResponseItem[],
        timeoutMs: number,
        observer: Observer<PowerShellBatchResponseItem[]>): void {
        if (timeoutMs && this.timestamp && (Date.now() - this.timestamp > timeoutMs)) {
            // force to cancel the command because of unexpected longer execution.
            this.raw.cancelCommand();
            this.timestamp = 0;
            return;
        }

        if (observer) {
            // return partial data if observer is not null.
            observer.next(psResponseList);
            this.currentData = psResponseList;
            return;
        }

        // Merge responses from calls which didn't complete in one go.
        for (const item of psResponseList) {
            // Check if we have saved record from previous call to add to.
            if (this.currentDataMap[item.nodeName]) {

                // Aggregate Results: If the newly received has results, aggregate them with saved data.
                if (item.properties.results) {

                    let array: any[];
                    // if any previously received results, use them in aggregation.
                    if (this.currentDataMap[item.nodeName].properties && this.currentDataMap[item.nodeName].properties.results) {

                        if (MsftSme.getTypeOf(this.currentDataMap[item.nodeName].properties.results) === 'array') {
                            array = this.currentDataMap[item.nodeName].properties.results;
                        } else {
                            array = [this.currentDataMap[item.nodeName].properties.results];
                        }

                    } else {
                        array = [];
                    }

                    // Add results from currently received data.
                    if (MsftSme.getTypeOf(item.properties.results) === 'array') {
                        item.properties.results.forEach((x: any) => {
                            array.push(x);
                        });
                    } else {
                        array.push(item.properties.results);
                    }

                    // Update saved map with the new aggregated data
                    this.currentDataMap[item.nodeName].properties.results = array;
                }

                // Aggregate Errors: If the newly received response has errors field, aggregate them with saved data.
                if (item.errors) {

                    let errorsArray: any[];
                    // if any previously received errors, use them in aggregation.
                    if (this.currentDataMap[item.nodeName].errors) {
                        errorsArray = this.currentDataMap[item.nodeName].errors;
                    } else {
                        errorsArray = [];
                    }

                    // Add results from currently received data.
                    item.errors.forEach((x: any) => {
                        errorsArray.push(x);
                    });

                    // Update saved map with the new aggregated data
                    this.currentDataMap[item.nodeName].errors = errorsArray;
                }

            } else {
                // first response/ no saved response. Simply add to map.
                this.currentDataMap[item.nodeName] = item;
            }
        }
        this.currentData = this.convertResponseMapDataToList(this.currentDataMap);
    }

    /**
     * Helper method to convert a map data to list
     *
     * @param nodeMap The map of nodenames to PowerShellBatchResponseItem. Used to track different calls in a batch.
     * @return The response data for the calls in a list.
     */
    private convertResponseMapDataToList(nodeMap: MsftSme.StringMap<PowerShellBatchResponseItem>): PowerShellBatchResponseItem[] {
        const responseList: PowerShellBatchResponseItem[] = [];

        for (const key in nodeMap) {
            if (nodeMap.hasOwnProperty(key)) {
                responseList.push(nodeMap[key]);
            }
        }
        return responseList;
    }

    /**
     * Attach lifetime object to disposer when disposing.
     *
     * @param lifetime The lifetime object.
     */
    private addLifetime(lifetime: DisposableLifetimeManager): void {
        const found: DisposableLifetimeManager = MsftSme.find(
            this.context.lifetimes, (value: DisposableLifetimeManager) => value === lifetime);
        if (!found) {
            this.context.lifetimes.push(lifetime);
            lifetime.registerForDispose(new Disposer(() => this.lifetimeDisposer(lifetime)));
        }
    }

    /**
     * Callback when disposing the container of view model.
     * If none, reference the PowerShell object. Dispose it. (Delete runspace)
     *
     * @param lifetime The lifetime object.
     */
    private lifetimeDisposer(lifetime: DisposableLifetimeManager): void {
        const found: DisposableLifetimeManager = MsftSme.find(
            this.context.lifetimes, (value: DisposableLifetimeManager) => value === lifetime);
        if (found) {
            MsftSme.remove(this.context.lifetimes, lifetime);
            if (this.context.lifetimes.length === 0) {
                // cancel queue command requests.
                this.queue.forEach((value: PowerShellBatchCommandItem) => {
                    value.observer.next(null);
                    value.observer.complete();
                });

                // delete from the map collection and delete the runspace/session.
                delete PowerShellBatch.map[PowerShellBatch.indexName(this.context.nodesList, this.context.key)];
                this.raw.dispose();
            }
        }
    }
}
